// Utilities
import Router from 'utils/router';

// Static classes
import Header from 'views/partials/common/header';
import Footer from 'views/partials/common/footer';

// Template classes
import Home from 'views/templates/home';

// Define templates
const TEMPLATES = {
  Home
};

// Manager Class
class Manager {

  /**
   * Constructor:
   * Manager constructor called in `main.js` file.
   *
   * @constructor
   */
  constructor() {
    // Root element
    this.$root = document.body;

    // Elements
    // this.$header = this.$root.querySelector('.site-header');
    // this.$footer = this.$root.querySelector('.site-footer');
    this.$container = this.$root.querySelector('.site-container');

    // Set `Router`
    this.Router = new Router();

    // `Router` state
    this.isNavigating = false;

    // `Router` events
    this._navigate = this.navigate.bind(this);
    this._navigateStart = this.onNavigateStart.bind(this);
    this._navigateEnd = this.onNavigateEnd.bind(this);
    this._navigateError = this.onNavigateError.bind(this);

    // `window` events
    this._resize = this.onResize.bind(this);

    // RAF event
    this._RAF = this.onRAF.bind(this);

    // Attach events
    this.attach();

    // Get template name
    const template = this.$container.getAttribute('template');

    // Initialization
    this.initStatic();
    this.initTemplate(template);
  }

  /**
   * Attach:
   * Attach and listen to events on DOM elements.
   */
  attach() {
    // Get all DOM internal links
    const $links = [...this.$root.querySelectorAll('[data-internal-link]')];

    // Attach `click` to internal links to navigate through `Router`
    for (let i = 0; i < $links.length; i++) {
      const $link = $links[i];
      const $children = [...$link.querySelectorAll('a')];

      for (let j = 0; j < $children.length; j++) {
        const $child = $children[j];

        // Stop propagation on child links
        $child.addEventListener('click', (e) => e.stopPropagation());
      }

      $link.addEventListener('click', this._navigate);
    }

    // Listen `Router` events
    this.Router.on(Router.NAVIGATE_START, this._navigateStart);
    this.Router.on(Router.NAVIGATE_END, this._navigateEnd);
    this.Router.on(Router.NAVIGATE_ERROR, this._navigateError);

    // Listen `window` events
    window.addEventListener('resize', this._resize);

    // Start RAF
    this.RAF = window.requestAnimationFrame(this._RAF);
  }

  /**
   * Init Static:
   * Initialize static classes like `Header` and `Footer`.
   */
  initStatic() {
    // Header
    if (this.$header) {
      this.Header = new Header({
        el: this.$header
      });
    }

    // Footer
    if (this.$footer) {
      this.Footer = new Footer({
        el: this.$footer
      });
    }
  }

  /**
   * Init Template:
   * Initialize template class based on template defined on container.
   *
   * @param {String} name - Template Name
   */
  initTemplate(name = null) {
    if (!name) {
      return;
    }

    // Parse template name
    const className = Manager.camelize(name);

    // Verify if `className` if defined
    if (TEMPLATES[className]) {
      // Initialize template class
      this.Template = new TEMPLATES[className]({
        el: this.$container
      });
    }
  }

  /**
   * Navigate:
   * Router Navigate
   *
   * @param {Object} event - Click Event
   * @return {void}
   */
  navigate(event) {
    // Prevent Default
    if (event) {
      if (event.preventDefault) {
        event.preventDefault();
      }
    }

    // Get clicked element
    const el = event.currentTarget;

    // Get element attributes
    const href = el.getAttribute('href') || el.getAttribute('data-href');
    const target = el.getAttribute('target') || el.getAttribute('data-target');

    // Remove focus on element
    el.blur();

    // Verify element target and open a new window when target is `_blank`
    if (target && target === '_blank') {
      return window.open(href);
    }

    // Avoid double navigation
    if (this.isNavigating) {
      return;
    }

    // Navigate through `Router`
    this.Router.navigate(href);
  }

  /**
   * Navigate Start:
   * Start `Router` navigation.
   */
  onNavigateStart() {
    // Update `Router` state
    this.isNavigating = true;
  }

  /**
   * Navigate End:
   * End `Router` navigation.
   *
   * @param {String} response - `Router` Response
   */
  onNavigateEnd(response) {
    // Parse `Router` response
    const parser = new DOMParser();
    const DOM = parser.parseFromString(response, 'text/html');

    // Get container from new page
    const container = DOM.querySelector('.site-container');

    // Replace content with new page's one
    this.$container.innerHTML = container.innerHTML;

    // Destroy current template class
    if (this.Template) {
      if (this.Template.destroy) {
        this.Template.destroy();
        this.Template = null;
      }
    }

    // Get new template name
    const name = container.getAttribute('template');

    // Update current template name with new one
    this.$container.setAttribute('view', name);

    // Initialize new template class
    this.initTemplate(name);

    // Reset `Router` state
    this.isNavigating = false;
  }

  /**
   * Navigate Error:
   * Error during `Router` navigation.
   *
   * @param {Object} error - `Router` Error
   * @param {String} response - `Router` Response
   */
  onNavigateError(error, response) {
    // Update `Router` state
    this.isNavigating = false;

    // Throw `Router` error in console
    throw new Error(response);
  }

  /**
   * Request Animation Frame:
   * Direct all RAF here.
   */
  onRAF() {
    // Header RAF
    if (this.Header && this.Header.onRAF) {
      this.Header.onRAF();
    }

    // Footer RAF
    if (this.Footer && this.Footer.onRAF) {
      this.Footer.onRAF();
    }

    // Template RAF
    if (this.Template && this.Template.onRAF) {
      this.Template.onRAF();
    }

    // RAF Loop
    this.RAF = window.requestAnimationFrame(this._RAF);
  }

  /**
   * Window resize:
   * Direct all resize events here.
   */
  onResize() {
    // Header resize
    if (this.Header && this.Header.onResize) {
      this.Header.onResize();
    }

    // Footer RAF
    if (this.Footer && this.Footer.onResize) {
      this.Footer.onResize();
    }

    // Template RAF
    if (this.Template && this.Template.onResize) {
      this.Template.onResize();
    }
  }

  /**
   * Converts string to UpperCamelCase
   *
   * @param {String} string - String to parse
   * @return {String} Parsed string
   * @static
   */
  static camelize(string) {
    return string.replace(/(?:^|[-_])(\w)/g, (_, c) => {
      return c ? c.toUpperCase() : '';
    });
  }
}

export default Manager;
